import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { queryKeys } from './queryKeys'

const host_url = process.env.REACT_APP_HOST_URL

/**
 * Fetches integration options from the API
 */
function useIntegrationObjects() {
   const query = useQuery({
      queryKey: queryKeys.integrationObjects,
      queryFn: async () => {
         const response = await fetch(`${host_url}/api/integration-objects`)
         if (!response.ok) {
            throw new Error('Failed to fetch integration objects')
         }
         let { integrationSolutions, integrationTypes } = await response.json()

         if (integrationSolutions && integrationTypes) {
            // Adds a None option to the integration types and solutions
            integrationTypes.unshift({ integration_code: 'None', name: 'None' })
            integrationSolutions.unshift({
               solution_code: 'None',
               solution_name: 'None',
            })
         }

         return { integrationSolutions, integrationTypes }
      },
      staleTime: 1000 * 60 * 60, // 1 hour
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      enabled: !!host_url,
   })

   // Show toast only when all retries have failed
   useEffect(() => {
      if (query.isError) {
         toast.error('Failed to fetch integration objects', { duration: 5000 })
      }
   }, [query.isError])

   return query
}

/**
 * Fetches time from the API
 */
function useTime() {
   const query = useQuery({
      queryKey: queryKeys.time,
      queryFn: async () => {
         const response = await fetch(`${host_url}/api/time`)
         if (!response.ok) {
            throw new Error('Failed to fetch time')
         }
         return response.json()
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      enabled: !!host_url,
   })

   // Show toast only when all retries have failed
   useEffect(() => {
      if (query.isError) {
         toast.error('Failed to fetch time', { duration: 5000 })
      }
   }, [query.isError])

   return query
}

/**
 * Fetches deal desk discounts for a specific opportunity
 * Only fetches when opportunityId is provided
 */
function useDealDeskDiscounts(opportunityId: string | null) {
   const query = useQuery({
      queryKey: queryKeys.dealDeskDiscounts(opportunityId || ''),
      queryFn: async () => {
         if (!opportunityId) {
            return { discounts: [], count: 0 }
         }
         const response = await fetch(
            `${host_url}/api/dealdesk-discount?opportunity_id=${opportunityId}`,
         )
         if (!response.ok) {
            throw new Error('Failed to fetch deal desk discounts')
         }
         const data = await response.json()
         return data.data || { discounts: [], count: 0 }
      },
      enabled: !!opportunityId, // Only fetch when opportunityId is set
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
   })

   // Show toast only when all retries have failed
   useEffect(() => {
      if (query.isError) {
         toast.error('Failed to fetch deal desk discounts', { duration: 5000 })
      }
   }, [query.isError])

   return query
}

export { useIntegrationObjects, useTime, useDealDeskDiscounts }
